import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MfaApi } from 'api/mfaApi';
import { ProfileApi } from 'api/profileAPI';
import { PopoutMenuHeader } from 'components/popupsAndPanels/PopoutMenuHeader';
import { Spinner } from 'components/spinner/Spinner';
import copyIcon from 'images/Icon_Mfa_Copy.png';
import copiedIcon from 'images/Icon_Mfa_Tick.png';
import shieldIcon from 'images/Icon_Mfa_Shield.png';
import {
  MFA_REQUIRED_CODE,
  MfaChallengeType,
  MfaGrantResult,
  MfaLayout,
  hasMessageCode,
  mfaCodeInstruction,
  mfaTypeLabel,
  supportedMfaChallengeTypes,
} from 'pages/mfa/models';
import { MFA_CODE_LENGTH, MfaCodeInput } from './MfaCodeInput';

export type MfaChallengeMode = 'verify' | 'enable' | 'disable';

type Props = {
  mode: MfaChallengeMode;
  layout: MfaLayout;
  challengeType?: MfaChallengeType;
  onComplete: (result: MfaGrantResult | null, setUp: boolean) => void;
  onCancel: () => void;
};

type Step = 'loading' | 'intro' | 'select' | 'totp' | 'code' | 'failed';

type TotpSetup = { qrCode: string; sharedSecret: string };

type SuspendedEnable = {
  challengeType: MfaChallengeType;
  mfaToken: string;
  totp: TotpSetup | null;
};

const UNAVAILABLE_MESSAGE =
  'No verification methods are available for your account.';
const GENERIC_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';
const PUPIL_INFO_REASON =
  'For security reasons, please verify your identity to access pupil information.';
const METHOD_CHOICE_LINES = [
  "To help protect pupil information, we just need to check it's really you.",
  "Choose how you'd like to verify your identity.",
];
const VERIFY_EXISTING_FIRST =
  'Verify with your existing method before adding a new one.';
const VERIFY_EMAIL_FIRST =
  'Before setting up the authentication app, please verify your email address.';
const CHECK_REASON =
  "To help keep pupil information safe, we just need to check it's really you.";
const APP_CODE_INSTRUCTION =
  'Open the authenticator app on your phone (such as Google Authenticator or Microsoft Authenticator) and enter the 6-digit code shown for Get Set 4 Education.';
const EMAIL_CODE_INSTRUCTION =
  "We've sent a verification code to your email address. Check your inbox and enter the code below.";

const loadChallengeTypes = async () => {
  const profile = await ProfileApi.getProfileDetails();
  return {
    ok: !!profile?.data,
    enabled: supportedMfaChallengeTypes(
      profile?.data?.enabledMfaChallengeTypes
    ),
    available: supportedMfaChallengeTypes(
      profile?.data?.availableMfaChallengeTypes
    ),
  };
};

export const MfaChallenge: React.FC<Props> = ({
  mode,
  layout,
  challengeType,
  onComplete,
  onCancel,
}) => {
  const [action, setAction] = useState<MfaChallengeMode>(mode);
  const [step, setStep] = useState<Step>('loading');
  const [firstStep, setFirstStep] = useState<Step | null>(null);
  const [options, setOptions] = useState<MfaChallengeType[]>([]);
  const [enrolling, setEnrolling] = useState(false);
  const [selectedType, setSelectedType] = useState<MfaChallengeType | null>(
    challengeType ?? null
  );
  const [mfaToken, setMfaToken] = useState('');
  const [totp, setTotp] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [suspendedEnable, setSuspendedEnable] =
    useState<SuspendedEnable | null>(null);
  const [verifyingEmailFirst, setVerifyingEmailFirst] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');
  const secretKeyRef = useRef<HTMLInputElement>(null);
  const copiedTimer = useRef<number>();

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      window.clearTimeout(copiedTimer.current);
    };
  }, []);

  useEffect(() => {
    window.clearTimeout(copiedTimer.current);
    setCopied(false);
    setCopyError('');
  }, [step]);

  const begin = useCallback(
    async (type: MfaChallengeType, currentAction: MfaChallengeMode) => {
      setCode('');
      setError('');
      setBusy(true);

      const wantsTotpSetup =
        currentAction === 'enable' && type === MfaChallengeType.Totp;
      const result = wantsTotpSetup
        ? await MfaApi.requestTotpQrCode()
        : await MfaApi.challenge(type);
      if (!mounted.current) return;

      // The method only changes once its token is in hand, so a failed switch leaves the current
      // method and token matched.
      if (result.data) {
        setSelectedType(type);
        setMfaToken(result.data.mfaToken);
        if (wantsTotpSetup && 'qrCode' in result.data) {
          setTotp({
            qrCode: result.data.qrCode,
            sharedSecret: result.data.sharedSecret,
          });
          setCopied(false);
          setStep('totp');
          setFirstStep((first) => first ?? 'totp');
        } else {
          setStep('code');
          setFirstStep((first) => first ?? 'code');
        }
      } else if (
        wantsTotpSetup &&
        hasMessageCode(result.error?.formErrors, MFA_REQUIRED_CODE)
      ) {
        setBusy(false);
        verifyBeforeEnableRef.current(type);
        return;
      } else {
        setError(result.error?.message ?? GENERIC_ERROR_MESSAGE);
        setStep((current) => (current === 'loading' ? 'failed' : current));
      }

      setBusy(false);
    },
    []
  );

  const verifyBeforeEnableRef = useRef<(type: MfaChallengeType) => void>(
    () => {}
  );

  useEffect(() => {
    const init = async () => {
      if (mode !== 'verify' && challengeType) {
        begin(challengeType, mode);
        return;
      }

      const types = await loadChallengeTypes();
      if (!mounted.current) return;
      if (!types.ok) {
        setError(GENERIC_ERROR_MESSAGE);
        setStep('failed');
        return;
      }

      const isEnrolling = types.enabled.length === 0;
      const list = isEnrolling ? types.available : types.enabled;
      setEnrolling(isEnrolling);
      setAction(isEnrolling ? 'enable' : 'verify');
      setOptions(list);

      if (list.length === 0) {
        setError(UNAVAILABLE_MESSAGE);
        setStep('failed');
      } else if (list.length === 1 && !isEnrolling) {
        begin(list[0], 'verify');
      } else {
        const opening = isEnrolling && layout === 'modal' ? 'intro' : 'select';
        setSelectedType(null);
        setStep(opening);
        setFirstStep(opening);
      }
    };

    init();
  }, [mode, layout, challengeType, begin]);

  const verifyBeforeEnable = async (type = selectedType) => {
    if (!type) return;
    setSuspendedEnable({ challengeType: type, mfaToken, totp });
    const types = await loadChallengeTypes();
    if (!mounted.current) return;

    // A user with no method yet proves their email address, which needs no set-up.
    const emailFirst = types.enabled.length === 0;
    const verifyWith = emailFirst
      ? types.available.filter((t) => t === MfaChallengeType.Email)
      : types.enabled;
    setVerifyingEmailFirst(emailFirst);
    setAction('verify');
    setOptions(verifyWith);
    setCode('');
    setTrustDevice(false);
    setBusy(false);

    if (!types.ok) {
      setError(GENERIC_ERROR_MESSAGE);
      setStep('failed');
    } else if (verifyWith.length === 1) {
      begin(verifyWith[0], 'verify');
    } else if (verifyWith.length > 1) {
      setSelectedType(null);
      setStep('select');
    } else {
      setError(UNAVAILABLE_MESSAGE);
      setStep('failed');
    }
  };

  verifyBeforeEnableRef.current = verifyBeforeEnable;

  const resumeEnable = (suspended: SuspendedEnable) => {
    setSuspendedEnable(null);
    setVerifyingEmailFirst(false);
    setAction('enable');
    // Refused before the app's set-up key was issued, so it is requested now that the user has verified.
    if (suspended.challengeType === MfaChallengeType.Totp && !suspended.totp) {
      setSelectedType(MfaChallengeType.Totp);
      setStep('loading');
      begin(MfaChallengeType.Totp, 'enable');
      return;
    }
    setSelectedType(suspended.challengeType);
    setMfaToken(suspended.mfaToken);
    setTotp(suspended.totp);
    setCode('');
    setError('');
    setStep('code');
    setBusy(false);
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType || code.length < MFA_CODE_LENGTH || busy) return;
    setBusy(true);
    setError('');

    const submission = { challengeType: selectedType, mfaToken, mfaCode: code };

    if (action === 'disable') {
      const result = await MfaApi.disableChallengeType(submission);
      if (!mounted.current) return;
      if (!result.error) {
        onComplete(null, false);
        return;
      }
      setError(result.error.message ?? GENERIC_ERROR_MESSAGE);
    } else if (action === 'enable') {
      const result = await MfaApi.enableChallengeType({
        ...submission,
        trustThisDevice: trustDevice,
      });
      if (!mounted.current) return;
      if (!result.error) {
        onComplete(result.data ?? null, enrolling);
        return;
      }
      if (hasMessageCode(result.error.formErrors, MFA_REQUIRED_CODE)) {
        await verifyBeforeEnable();
        return;
      }
      setError(result.error.message ?? GENERIC_ERROR_MESSAGE);
    } else {
      const result = await MfaApi.verify({
        ...submission,
        trustThisDevice: trustDevice,
      });
      if (!mounted.current) return;
      if (!result.error) {
        if (suspendedEnable) {
          resumeEnable(suspendedEnable);
        } else {
          onComplete(result.data ?? null, false);
        }
        return;
      }
      setError(result.error.message ?? GENERIC_ERROR_MESSAGE);
    }

    setCode('');
    setBusy(false);
  };

  // The clipboard API is missing on insecure origins and refused in some frames, so fall back to copying the selection.
  const copySecret = async () => {
    if (!totp?.sharedSecret) return;
    let bCopied: boolean;
    try {
      await navigator.clipboard.writeText(totp.sharedSecret);
      bCopied = true;
    } catch {
      secretKeyRef.current?.select();
      bCopied = document.execCommand('copy');
    }
    if (!mounted.current) return;
    if (bCopied) {
      setCopyError('');
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
    } else {
      setCopyError('Copy the key above instead');
    }
  };

  // The pupil-tool gate (page or pop-up) asks for the reason and offers trust; profile changes do not.
  const isGate = mode === 'verify';
  const settingUpType = suspendedEnable?.challengeType ?? selectedType;
  const otherType = options.find((type) => type !== selectedType);
  const isCodeCheck =
    isGate && action === 'verify' && !suspendedEnable && step === 'code';

  const title = (() => {
    if (step === 'intro') return 'Multi factor authentication required';
    if (step === 'totp') return 'Set up your authentication app';
    if (isCodeCheck) return 'Verify your identity';
    if (!isGate && settingUpType) {
      const label = mfaTypeLabel(settingUpType).toLowerCase();
      return action === 'disable' && !suspendedEnable
        ? `Disable ${label} MFA`
        : `Setup ${label} MFA`;
    }
    if (isGate && step === 'select' && !suspendedEnable) {
      return "Let's keep pupil information safe";
    }
    return 'Multi factor authentication';
  })();

  const introLines: string[] = (() => {
    if (step === 'select') {
      return suspendedEnable
        ? [
            VERIFY_EXISTING_FIRST,
            'Choose how you would like to verify your identity.',
          ]
        : METHOD_CHOICE_LINES;
    }
    if (step === 'totp') {
      return [
        'Open an authenticator app, such as Google Authenticator or Microsoft Authenticator, on your phone and follow these steps:',
      ];
    }
    if (isCodeCheck && selectedType === MfaChallengeType.Totp) {
      return [CHECK_REASON, APP_CODE_INSTRUCTION];
    }
    if (isCodeCheck && selectedType === MfaChallengeType.Email) {
      return [CHECK_REASON, EMAIL_CODE_INSTRUCTION];
    }
    if (step === 'code' && selectedType) {
      const instruction = mfaCodeInstruction(selectedType, layout);
      if (suspendedEnable) {
        return [
          verifyingEmailFirst ? VERIFY_EMAIL_FIRST : VERIFY_EXISTING_FIRST,
          instruction,
        ];
      }
      if (isGate && action === 'verify')
        return [PUPIL_INFO_REASON, instruction];
      return [instruction];
    }
    return [];
  })();

  const showBackLink =
    layout === 'page' && (step === firstStep || step === 'failed');
  const switchTo =
    isGate &&
    !suspendedEnable &&
    !isCodeCheck &&
    (step === 'totp' || step === 'code')
      ? otherType
      : undefined;

  // Email needs no set-up, so anyone can verify with it. The app is only offered once it is set up.
  const codeCheckFallback = !isCodeCheck
    ? undefined
    : selectedType === MfaChallengeType.Totp
    ? {
        prompt: "Can't use your authenticator app?",
        link: 'Verify by email instead.',
        type: MfaChallengeType.Email,
      }
    : selectedType === MfaChallengeType.Email &&
      options.includes(MfaChallengeType.Totp)
    ? {
        prompt: "Can't access your email?",
        link: 'Use an authenticator app instead.',
        type: MfaChallengeType.Totp,
      }
    : undefined;

  const codeCheckSwitch = codeCheckFallback && (
    <p className="MfaSwitch">
      {codeCheckFallback.prompt}{' '}
      <button
        type="button"
        className="MfaLink"
        onClick={() => begin(codeCheckFallback.type, 'verify')}
        disabled={busy}
      >
        {codeCheckFallback.link}
      </button>
    </p>
  );

  const switchLink =
    switchTo &&
    (step === 'totp' && switchTo === MfaChallengeType.Email ? (
      <p className="MfaSwitch">
        <button
          type="button"
          className="MfaLink"
          onClick={() => begin(switchTo, action)}
          disabled={busy}
        >
          Prefer to receive a code by email instead?
        </button>
      </p>
    ) : (
      <p className="MfaSwitch">
        Use{' '}
        <button
          type="button"
          className="MfaLink"
          onClick={() => begin(switchTo, action)}
          disabled={busy}
        >
          {mfaTypeLabel(switchTo).toLowerCase()}
        </button>{' '}
        instead?
      </p>
    ));

  const body = (
    <>
      {introLines.map((line) => (
        <p className="MfaIntro" key={line}>
          {line}
        </p>
      ))}

      {step === 'loading' && (
        <div className="MfaSpinner">
          <Spinner />
        </div>
      )}

      {step === 'failed' && <div className="MfaError">{error}</div>}

      {step === 'intro' && (
        <div className="MfaRequired">
          <div className="MfaRequiredIcon">
            <img src={shieldIcon} alt="" />
          </div>
          <p className="MfaRequiredLead">
            To proceed with this action, you’ll need to set up multi factor
            authentication (MFA) on your account.
          </p>
          <p className="MfaIntro">
            This extra security step helps protect your information and ensures
            the process remains secure.
          </p>
          <button
            type="button"
            className="Btn BtnOrange MfaPrimary"
            onClick={() => setStep('select')}
          >
            Setup MFA
          </button>
        </div>
      )}

      {step === 'select' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (selectedType) begin(selectedType, action);
          }}
        >
          <div className="MfaMethodList" role="radiogroup">
            {options.map((type) => (
              <label
                key={type}
                className={`MfaMethodRow ${
                  selectedType === type ? 'Selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="mfa-type"
                  value={type}
                  checked={selectedType === type}
                  onChange={() => setSelectedType(type)}
                  disabled={busy}
                />
                <span className="MfaMethodTick" aria-hidden="true" />
                <span className="MfaMethodLabel">
                  {type === MfaChallengeType.Email
                    ? 'Email verification code'
                    : mfaTypeLabel(type)}
                </span>
              </label>
            ))}
          </div>
          {error && <div className="MfaError">{error}</div>}
          <button
            type="submit"
            className="Btn BtnOrange MfaPrimary"
            disabled={busy || !selectedType}
          >
            {busy ? <Spinner size={8} color="#ffffff" /> : 'Continue'}
          </button>
        </form>
      )}

      {step === 'totp' && totp && (
        <>
          <ol className="MfaSteps">
            <li>Tap + in the app to add a new account.</li>
            <li>Scan the QR code below.</li>
            <li>
              Select Continue to enter the verification code shown in your app.
            </li>
          </ol>
          <div className="MfaQrCode">
            <img
              src={`data:image/png;base64,${totp.qrCode}`}
              alt="Authenticator app QR code"
            />
          </div>
          <p className="MfaIntro">
            Can't scan the QR code? Copy this setup key and enter it manually in
            your authentication app.
          </p>
          <div className="MfaSecretKey">
            <input
              ref={secretKeyRef}
              type="text"
              readOnly
              value={totp.sharedSecret}
              aria-label="Setup key"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="MfaCopy"
              onClick={copySecret}
              title="Copy key"
            >
              <img
                src={copied ? copiedIcon : copyIcon}
                alt={copied ? 'Copied' : 'Copy key'}
                className={copied ? 'MfaCopied' : undefined}
              />
            </button>
          </div>
          {copyError && (
            <p className="MfaCopyStatus" role="status">
              {copyError}
            </p>
          )}
          {error && <div className="MfaError">{error}</div>}
          <button
            type="button"
            className="Btn BtnOrange MfaPrimary"
            onClick={() => setStep('code')}
            disabled={busy}
          >
            Continue
          </button>
          {switchLink}
        </>
      )}

      {step === 'code' && selectedType && (
        <form onSubmit={submitCode}>
          <div className="MfaCodeHeader">
            <span>Your verification code</span>
            {selectedType === MfaChallengeType.Totp ? (
              action === 'enable' &&
              totp && (
                <button
                  type="button"
                  className="MfaLink"
                  onClick={() => setStep('totp')}
                >
                  Show QR code
                </button>
              )
            ) : (
              <button
                type="button"
                className="MfaLink"
                onClick={() => begin(selectedType, action)}
                disabled={busy}
              >
                Resend code
              </button>
            )}
          </div>

          <MfaCodeInput value={code} onChange={setCode} disabled={busy} />

          {isGate && action !== 'disable' && !suspendedEnable && (
            <label className="MfaTrustDevice">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                disabled={busy}
              />
              <span className="MfaTrustBox" aria-hidden="true" />
              <span>Trust this device for 30 days</span>
            </label>
          )}

          {error && <div className="MfaError">{error}</div>}

          <button
            type="submit"
            className="Btn BtnOrange MfaPrimary"
            disabled={busy || code.length < MFA_CODE_LENGTH}
          >
            {busy ? (
              <Spinner size={8} color="#ffffff" />
            ) : isGate ? (
              'Verify'
            ) : (
              'Continue'
            )}
          </button>
          {switchLink}
          {codeCheckSwitch}
        </form>
      )}
    </>
  );

  if (layout === 'modal') {
    return (
      <>
        <PopoutMenuHeader title={title} handleClose={onCancel} />
        <div className="PopoutMenuContent MfaChallenge MfaModalLayout">
          {body}
        </div>
      </>
    );
  }

  return (
    <div className="MfaChallenge MfaPageLayout">
      {showBackLink && (
        <button type="button" className="MfaBackLink" onClick={onCancel}>
          &larr; <span>Back to previous page</span>
        </button>
      )}
      <h1>{title}</h1>
      {body}
    </div>
  );
};

export default MfaChallenge;
